import React, { useEffect, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { additionalInformation } from "us.collection.debtor/actions";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { RootState } from "us.helper/types";
import { PlusOutlined, MoreOutlined } from "us.icons";
import { AdditionalInfoDetails, ItemMenu } from "./Components";
import { AdditionalInfoAction } from "./Constants";
import {
  DEFAULT_DRAWER_STATE,
  DrawerType,
} from "us.collection.debtor/constants/AdditionalInformation";
import "./Home.scss";

const {
  $Skeleton,
  $TableTree,
  $DateLabel,
  $Tooltip,
  $Button,
  $Drawer,
  $Popover,
} = Common.Components;

/**
 * @description - Additional information main component
 * @link Design Document - https://unicorn-solutions.atlassian.net/browse/UBX4-23262
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 04/11/2024
 */
const AdditionalInformation: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation(["US.COLLECTION.DEBTOR"]);

  const {
    informationList,
    metaData,
    drawerInfo,
    resetAll,
    manageDrawer,
    getAdditionalInformation,
    deleteAdditionalInformation,
  } = props;

  const { debtorEntNumber } = metaData.data ?? {}
  const [itemMenu, setItemMenu] = useState<{
    visible: boolean;
    infoId: number;
  }>({ visible: false, infoId: -1 });

  useEffect(() => {
    if (metaData && debtorEntNumber) {
      getAdditionalInformation &&
        getAdditionalInformation(debtorEntNumber);
    }
    return () => {
      resetAll && resetAll({})
    };
  }, [metaData]);

  // auto close item menu after 5 seconds
  const setAutoClose = (infoId: number) => {
    setTimeout(() => {
      setItemMenu({
        visible: false,
        infoId,
      });
    }, 8000);
  };

  const columns: any = [
    {
      title: "",
      key: "more",
      dataIndex: "more",
      width: 50,
      customRenderChild: (text: any, record: any) => {
        return (
          <div className="align-items-center" key={0}>
            <$Popover
              placement="bottomLeft"
              trigger="click"
              open={itemMenu.visible && itemMenu.infoId === record.id}
              onOpenChange={(visible: boolean) => {
                setItemMenu({
                  ...itemMenu,
                  visible,
                  infoId: record.id,
                });
                setAutoClose(0);
              }}
              content={
                <ItemMenu
                  actions={
                    !record?.isEditable
                      ? [AdditionalInfoAction.EDIT]
                      : undefined
                  }
                  isEditable={record?.isEditable}
                  onCallAction={(actionType: AdditionalInfoAction) => {
                    setItemMenu({
                      ...itemMenu,
                      visible: false,
                      infoId: record.id,
                    });
                    onHandleTableAction(actionType, record);
                  }}
                />
              }
              destroyTooltipOnHide
            >
              <$Button
                data-testid="popover-btn"
                icon={<MoreOutlined />}
                size="small"
              />
            </$Popover>
          </div>
        );
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.STATUS"),
      dataIndex: "status",
      key: "status",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: { showTitle: false },
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.MODIFIED_USER"),
      dataIndex: "modifiedUser",
      key: "modifiedUser",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: { showTitle: false },
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.MODIFIED_DATE"),
      dataIndex: "modifiedDate",
      key: "modifiedDate",
      width: "300px",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: "date",
      customRenderChild: (text: any) => {
        return (
          <span>
            <$DateLabel value={text} />
          </span>
        );
      },
    },
  ];

  /**
   * @description handle information menu actions
   * @param action - action of clicked menu icon
   * @param record - selected information for action
   */
  const onHandleTableAction = (action: AdditionalInfoAction, record: any) => {
    const { id } = record;
    switch (action) {
      case AdditionalInfoAction.EDIT:
        openDrawer(DrawerType.EDIT_ADDITIONAL_INFO, id, record?.isEditable, record);
        break;
      case AdditionalInfoAction.DELETE:
        deleteAdditionalInformation &&
          deleteAdditionalInformation({
            debtorNo: metaData.data?.debtorEntNumber,
            infoId: id,
          });
        break;
      default:
        break;
    }
  };

  /**
   * @description Open the drawer by type
   * @param {DrawerType} type - Type of the drawer
   * @param {number} infoId - Selected information id
   */
  const openDrawer = (
    type: DrawerType,
    infoId: number,
    isEditable?: boolean,
    record?: any
  ) => {
    // decide the suitable title for the drawer
    let title =
      type === DrawerType.EDIT_ADDITIONAL_INFO
        ? t(
            `US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.${
              isEditable ? "EDIT_" : "VIEW_"
            }ADDITIONAL_INFORMATION`
          )
        : t(
            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.NEW_ADDITIONAL_INFORMATION"
          );

    // change drawer state
    manageDrawer &&
      manageDrawer({
        type,
        title,
        infoId,
        isEditable,
        record,
      });
  };

  /**
   * @description - Close the drawer
   */
  const closeDrawer = () => {
    manageDrawer && manageDrawer(DEFAULT_DRAWER_STATE);
  };

  return (
    <div className="additional-info">
      <$Button
        icon={<PlusOutlined />}
        size="small"
        className="mt-1"
        disabled={informationList?.isLoading}
        onClick={() => openDrawer(DrawerType.NEW_ADDITIONAL_INFO, -1)}
      >
        {t("US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADD_NEW")}
      </$Button>
      <$Skeleton
        loading={informationList?.isLoading}
        active
        paragraph={{ rows: 2 }}
      >
        <$TableTree
          rowKey="id"
          data={informationList?.data}
          size="small"
          className="mt-3"
          onSort={(sortData, dataSource) => {
            return sortData(dataSource);
          }}
          onFilter={(searchData, dataSource) => {
            return searchData(dataSource);
          }}
          filterOnType={true}
          resetOnSourceChange={true}
          bordered
          pagination={false}
          virtual={true}
          scroll={{
            x: 1400,
            y: "calc(100vh - 280px)",
          }}
          columns={columns}
          firstColSkipFilterProps={-1}
          onRow={(record, _rowIndex) => {
            return {
              onDoubleClick: () =>
                onHandleTableAction(AdditionalInfoAction.EDIT, record),
            };
          }}
        />
      </$Skeleton>

      <$Drawer
        title={drawerInfo?.title}
        width={"800px"}
        open={drawerInfo?.visible}
        onClose={closeDrawer}
        destroyOnClose={true}
        maskClosable={false}
      >
        <AdditionalInfoDetails onCancel={closeDrawer} />
      </$Drawer>
    </div>
  );
};

const mapState = (state: RootState) => {
  const { common, domainView, additionalInformation } = state;
  const { informationList, drawerInfo } = additionalInformation;
  const { metaData } = domainView;

  return {
    metaData,
    currentLanguage: common.currentLanguage,
    informationList,
    drawerInfo,
  };
};

const { informationList, deleteInformation } = additionalInformation;
const mapDispatch = {
  resetAll: informationList.reset,
  manageDrawer: informationList.openDrawer,
  getAdditionalInformation: informationList.get,
  deleteAdditionalInformation: deleteInformation.save,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AdditionalInformation);
